import { Phone, ClipboardCheck, ShieldCheck, Truck, Clock, ArrowUpRight } from "lucide-react";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F2EDE2";
const INK = "#1A1F2E";
const INK_MUTED = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const FONT_MONO = "ui-monospace, 'SF Mono', 'JetBrains Mono', Menlo, monospace";
type Step = {
  num: string;
  time: string;
  tag: string;
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  description: string;
  insetLabel: string;
  insetText: string;
};
const STEPS: Step[] = [{
  num: "01",
  time: "00:00",
  tag: "Phone Diagnosis",
  icon: Phone,
  title: "Real tech answers. Confirms the spring in 60 seconds.",
  description: "Real technician picks up — not a call centre. Tell us what you heard, what you see, and what the door is doing. We'll confirm spring failure over the phone and warn you not to operate the door until we arrive.",
  insetLabel: "What you'll see",
  insetText: "SMS with technician name, ETA, and the spring sizes we're bringing for your door brand."
}, {
  num: "02",
  time: "00:30",
  tag: "Van On The Way",
  icon: Truck,
  title: "Stocked van dispatched immediately.",
  description: "Our service vans carry every common torsion and extension spring size, plus replacement cables, drums, and bearings. The right parts ride with us — no return trip, no waiting on suppliers.",
  insetLabel: "On board",
  insetText: "Torsion & extension springs for B&D, Steel-Line, Gliderol, Merlin, ATA, DoorWorks all stocked."
}, {
  num: "03",
  time: "00:40",
  tag: "On-Site Inspection",
  icon: ClipboardCheck,
  title: "Measure, inspect, fixed-price quote.",
  description: "We measure your existing springs (inner diameter, wire gauge, length), inspect cables and drums for collateral damage from the spring failure, and confirm whether one or both springs need replacing. Written fixed-price quote before any tool comes out.",
  insetLabel: "Full inspection",
  insetText: "Both springs measured & matched. Cables, drums, rollers checked for collateral damage."
}, {
  num: "04",
  time: "01:30",
  tag: "Replaced & Tested",
  icon: ShieldCheck,
  title: "Matched pair installed, balance tested, warranted.",
  description: "Springs replaced as a matched pair (industry best practice — one is always next), door balance tested at 4 positions, cycled 5–10 times to confirm safe operation. AS 4505 compliance documented and warranty card handed over.",
  insetLabel: "Safety check",
  insetText: "12-month workmanship warranty + parts manufacturer warranty in writing on every job."
}];
export default function SpringProcess() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY
  }} aria-labelledby="spring-process-heading">
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.05 0 0 0 0 0.08 0 0 0 0 0.22 0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      <div aria-hidden className="absolute -top-32 -right-32 w-[450px] h-[450px] rounded-full pointer-events-none opacity-[0.06]" style={{
      backgroundColor: GOLD,
      filter: "blur(80px)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 md:pt-32 pb-20 md:pb-28">

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 md:mb-20 items-end">
          <div className="lg:col-span-8">
            <div className="mb-5 inline-flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border" style={{
              borderColor: `${GOLD_DARK}66`,
              backgroundColor: `${GOLD}1A`
            }}>
                <Clock className="h-3.5 w-3.5" strokeWidth={2.5} style={{
                color: GOLD_DARK
              } as React.CSSProperties} />
              </span>
              <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{
              color: GOLD_DARK
            }}>
                The Spring Replacement Process
              </span>
            </div>

            <h2 id="spring-process-heading" className="font-extrabold" style={{
            fontFamily: FONT_HEAD,
            color: NAVY,
            fontSize: "clamp(2rem, 5vw, 4rem)",
            lineHeight: 1.02,
            letterSpacing: "-0.03em"
          }}>
              From the bang to a working door &mdash;
              <br className="hidden md:block" />
              <span style={{
              color: GOLD_DARK
            }}>usually inside 2 hours.</span>
            </h2>
          </div>

          <div className="lg:col-span-4">
            <p className="text-[15px] md:text-base leading-relaxed" style={{
            color: INK_MUTED
          }}>
              Every spring replacement follows the same four-step process &mdash;
              phone confirmation, fixed-price quote on-site, matched-pair install,
              balance test in writing.
            </p>

            <div className="mt-5 flex items-center gap-3">
              <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5" style={{
              backgroundColor: NAVY,
              fontFamily: FONT_MONO
            }}>
                <span className="relative flex h-1.5 w-1.5" aria-hidden>
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{
                  backgroundColor: GOLD
                }} />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{
                  backgroundColor: GOLD
                }} />
                </span>
                <span className="text-[10px] font-bold tracking-[0.2em]" style={{
                color: GOLD
              }}>T + 00:30</span>
              </div>
              <span className="text-[10px] font-semibold tracking-[0.18em] uppercase" style={{
              color: INK_MUTED
            }}>
                avg. elapsed time
              </span>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="relative">
          <div aria-hidden className="absolute left-[22px] md:left-[88px] top-3 bottom-3 w-px hidden sm:block" style={{
          background: `linear-gradient(180deg, ${GOLD_DARK}66 0%, ${NAVY}26 50%, ${GOLD_DARK}66 100%)`
        }} />

          <div className="space-y-12 md:space-y-14">
            {STEPS.map((step, idx) => <StepRow key={step.num} step={step} index={idx} isLast={idx === STEPS.length - 1} />)}
          </div>
        </div>

        {/* Guarantee strip */}
        <div className="mt-20 md:mt-24">
          <div className="relative rounded-2xl border overflow-hidden p-7 md:p-9" style={{
          backgroundColor: "white",
          borderColor: `${NAVY}1A`,
          boxShadow: `0 20px 60px -20px ${NAVY}26`
        }}>
            <div aria-hidden className="absolute top-0 left-8 right-8 h-[2px]" style={{
            background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD}80 50%, transparent 100%)`
          }} />

            <div className="flex items-center justify-between gap-6 flex-wrap">
              <div className="flex items-center gap-5 flex-1 min-w-[260px]">
                <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-2xl" style={{
                background: `linear-gradient(135deg, ${GOLD}26 0%, ${GOLD}0D 100%)`,
                border: `1px solid ${GOLD}40`
              }}>
                  <ShieldCheck className="h-6 w-6" strokeWidth={2} style={{
                  color: GOLD_DARK
                } as React.CSSProperties} />
                </div>
                <div>
                  <div className="text-[10px] font-bold tracking-[0.28em] uppercase mb-1" style={{
                  color: GOLD_DARK
                }}>
                    Our Guarantee
                  </div>
                  <h3 className="font-bold" style={{
                  fontFamily: FONT_HEAD,
                  color: NAVY,
                  fontSize: "clamp(1.125rem, 2vw, 1.5rem)",
                  lineHeight: 1.2,
                  letterSpacing: "-0.015em"
                }}>
                    No safety pass? We come back free.
                  </h3>
                </div>
              </div>

              <a href="tel:0485000933" className="group inline-flex items-center gap-2.5 rounded-full px-6 py-3 text-[14px] font-bold transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]" style={{
              backgroundColor: NAVY,
              color: "white",
              fontFamily: FONT_BODY
            }} onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = GOLD_DARK;
            }} onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = NAVY;
            }}>
                <Phone className="h-4 w-4" strokeWidth={2.5} />
                Book My Spring Replacement
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function StepRow({
  step,
  index
}: {
  step: Step;
  index: number;
  isLast: boolean;
}) {
  const Icon = step.icon;
  const isFinal = index === 3;
  return <div className="relative grid grid-cols-1 sm:grid-cols-[44px_1fr] md:grid-cols-[176px_1fr] gap-x-6 md:gap-x-10 gap-y-3 group">

      <div className="relative flex items-center sm:flex-col sm:items-start gap-4 sm:gap-0">
        <div className="hidden md:flex items-center gap-2 mb-3" style={{
        fontFamily: FONT_MONO
      }}>
          <span className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{
          color: INK_MUTED
        }}>
            T +
          </span>
          <span className="text-base font-bold tabular-nums" style={{
          color: NAVY,
          letterSpacing: "0.05em"
        }}>
            {step.time}
          </span>
        </div>

        <div className="relative z-10 flex items-center sm:items-start">
          <div className="relative flex items-center justify-center w-11 h-11 rounded-full border-2 transition-all duration-300" style={{
          backgroundColor: isFinal ? GOLD : CREAM,
          borderColor: isFinal ? GOLD : GOLD_DARK,
          boxShadow: isFinal ? `0 0 0 4px ${CREAM}, 0 0 0 5px ${GOLD}40` : `0 0 0 4px ${CREAM}`
        }}>
            <Icon className="h-[18px] w-[18px]" strokeWidth={2.2} style={{
            color: isFinal ? NAVY : GOLD_DARK
          } as React.CSSProperties} />
          </div>
        </div>

        <div className="md:hidden inline-flex items-center gap-1.5 rounded-full px-2.5 py-1" style={{
        backgroundColor: NAVY,
        fontFamily: FONT_MONO
      }}>
          <span className="text-[10px] font-bold tracking-[0.15em]" style={{
          color: GOLD
        }}>
            T+{step.time}
          </span>
        </div>
      </div>

      <div className="pt-1 pb-2">
        <div className="flex items-center gap-3 mb-2.5">
          <span className="font-extrabold leading-none" style={{
          fontFamily: FONT_HEAD,
          color: `${GOLD_DARK}66`,
          fontSize: "clamp(1.25rem, 1.8vw, 1.625rem)",
          letterSpacing: "-0.02em",
          fontVariantNumeric: "tabular-nums"
        }}>
            {step.num}
          </span>
          <span className="h-px w-6" style={{
          backgroundColor: `${INK_MUTED}40`
        }} />
          <span className="text-[10px] font-bold tracking-[0.24em] uppercase" style={{
          color: GOLD_DARK
        }}>
            {step.tag}
          </span>
        </div>

        <h3 className="font-bold mb-3" style={{
        fontFamily: FONT_HEAD,
        color: NAVY,
        fontSize: "clamp(1.25rem, 2.2vw, 1.625rem)",
        lineHeight: 1.2,
        letterSpacing: "-0.02em"
      }}>
          {step.title}
        </h3>

        <p className="text-[15px] leading-[1.65] mb-5 max-w-2xl" style={{
        color: INK
      }}>
          {step.description}
        </p>

        <div className="inline-flex items-start gap-3 rounded-lg px-4 py-3 border max-w-2xl" style={{
        backgroundColor: CREAM_DEEP,
        borderColor: `${GOLD_DARK}26`
      }}>
          <div className="flex-shrink-0 w-1 h-full rounded-full self-stretch min-h-[28px]" style={{
          backgroundColor: GOLD
        }} />
          <div>
            <div className="text-[10px] font-bold tracking-[0.22em] uppercase mb-1" style={{
            color: GOLD_DARK
          }}>
              {step.insetLabel}
            </div>
            <p className="text-[13px] leading-snug font-medium" style={{
            color: INK
          }}>
              {step.insetText}
            </p>
          </div>
        </div>
      </div>
    </div>;
}